import React, { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, ResponsiveContainer,
} from "recharts";

const CL = new Intl.NumberFormat("es-CL");

// Fondo del panel (usa el de tu app)
const CARD_BG = "transparent";
const CARD_BORDER = "#2a2a2a";

// Tooltip más claro (gris medio)
const TOOLTIP_BG = "#1f2937";    // slate-800
const TOOLTIP_BORDER = "#4b5563"; // slate-600

// Orden: Flete -> Comisión -> Rechazo (sin ganancia_total)
const FIELDS = [
  { key: "ganancia_flete",    label: "Flete",    color: "#0ea5e9" },
  { key: "ganancia_comision", label: "Comisión", color: "#a855f7" },
  { key: "ganancia_rechazo",  label: "Rechazo",  color: "#ef4444" },
];

function MonthlyTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;

  const row = payload[0]?.payload || {};
  const total =
    (typeof row.ganancia_total === "number" ? row.ganancia_total : 0) ||
    FIELDS.reduce((acc, f) => acc + (row[f.key] ?? 0), 0);

  const items = payload.filter((p) => (p.value ?? 0) > 0);

  return (
    <div
      style={{
        backgroundColor: TOOLTIP_BG,
        border: `1px solid ${TOOLTIP_BORDER}`,
        borderRadius: 8,
        padding: "8px 10px",
        color: "#F3F4F6",
        minWidth: 200,
        boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {items.map((p) => {
        const v = Number(p.value) || 0;
        const pct = total > 0 ? (v / total) * 100 : 0;
        return (
          <div key={p.dataKey} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 2 }}>
            <span style={{ width: 10, height: 10, background: p.color, borderRadius: 2 }} />
            <span style={{ flex: 1 }}>{p.name}</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {CL.format(v)} ({pct.toFixed(1)}%)
            </span>
          </div>
        );
      })}
      <div
        style={{
          borderTop: `1px solid ${TOOLTIP_BORDER}`,
          marginTop: 6,
          paddingTop: 6,
          display: "flex",
          justifyContent: "space-between",
          fontWeight: 600,
          color: "#e5e7eb",
        }}
      >
        <span>Total</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{CL.format(total)}</span>
      </div>
    </div>
  );
}

export default function IngresosMensualCard({ data }) {
  const [mode, setMode] = useState("grouped"); // "grouped" | "stacked"

  return (
    <div
      className="rounded-xl p-4 mb-6"
      style={{ background: CARD_BG, border: `1px solid ${CARD_BORDER}` }}
    >
      <div className="flex items-center gap-3 mb-3">
        <h3 className="text-gray-100 text-lg font-semibold">💰 Ganancias mensuales</h3>
        <div className="ml-auto flex gap-2">
          <button
            onClick={() => setMode("grouped")}
            className={`px-3 py-1 rounded-lg text-sm font-semibold border ${
              mode === "grouped" ? "bg-gray-100 text-gray-900 border-gray-100" : "bg-transparent text-gray-200"
            }`}
            style={{ borderColor: mode === "grouped" ? "#e5e7eb" : CARD_BORDER }}
          >
            Barras separadas
          </button>
          <button
            onClick={() => setMode("stacked")}
            className={`px-3 py-1 rounded-lg text-sm font-semibold border ${
              mode === "stacked" ? "bg-gray-100 text-gray-900 border-gray-100" : "bg-transparent text-gray-200"
            }`}
            style={{ borderColor: mode === "stacked" ? "#e5e7eb" : CARD_BORDER }}
          >
            Apilado
          </button>
        </div>
      </div>

      <div style={{ background: CARD_BG, borderRadius: 12 }}>
        <ResponsiveContainer width="100%" height={420}>
          <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 36 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={CARD_BORDER} />
            <XAxis dataKey="mes" tick={{ fill: "#E5E7EB" }} interval={0} height={32} />
            <YAxis tick={{ fill: "#E5E7EB" }} tickFormatter={(v) => CL.format(v)} />
            <Tooltip content={<MonthlyTooltip />} cursor={{ fill: "rgba(255,255,255,0.06)" }} />
            <Legend wrapperStyle={{ color: "#E5E7EB" }} />
            {FIELDS.map((f) => (
              <Bar
                key={f.key}
                dataKey={f.key}
                name={f.label}
                fill={f.color}
                radius={[6, 6, 0, 0]}
                barSize={mode === "grouped" ? 36 : 42}
                stackId={mode === "stacked" ? "a" : undefined}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
